import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../services/api';
import './PanchangList.css';



// Icons for each section - using consistent orange theme
const SECTION_CONFIG = {
  'त्योहार': {
    icon: '🎊',
    description: 'Festivals & Celebrations'
  },
  'व्रत': {
    icon: '🙏',
    description: 'Fasting Days'
  },
  'मुहूर्त': {
    icon: '⏰',
    description: 'Auspicious Timings'
  },
  'ग्रहण': {
    icon: '🌙',
    description: 'Eclipse Events'
  }
};

// Consistent orange theme for all cards
const CARD_THEME = {
  gradient: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
  bgLight: 'rgba(255, 107, 53, 0.1)',
  color: '#ff6b35'
};

const getDefaultConfig = (index) => ({
  icon: ['🕉️', '📿', '🪔', '✨'][index % 4],
  description: 'Panchang Events'
});

const PanchangList = () => {
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [sectionDetails, setSectionDetails] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    dates: [{ date: '', in_time: '', out_time: '', description: '' }]
  });

  // Helper to convert YYYY-MM-DD (calendar) to DD-MM-YYYY (display/API)
  const formatDateForAPI = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  };

  // Helper to convert DD-MM-YYYY (API) to YYYY-MM-DD (calendar)
  const formatDateForCalendar = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('-');
    if (parts.length !== 3) return '';
    const [day, month, year] = parts;
    return `${year}-${month}-${day}`;
  };

  // Helper to convert 24-hour format (HH:MM) to 12-hour format (H:MM AM/PM)
  const convert24to12 = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Helper to convert 12-hour format (H:MM AM/PM) to 24-hour format (HH:MM)
  const convert12to24 = (time12) => {
    if (!time12) return '';
    const match = time12.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return time12; // Return as-is if format doesn't match
    let [, hours, minutes, period] = match;
    hours = parseInt(hours);
    if (period.toUpperCase() === 'PM' && hours !== 12) hours += 12;
    if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;
    return `${hours.toString().padStart(2, '0')}:${minutes}`;
  };

  // Helper to combine in_time and out_time to time_frame (with AM/PM format)
  const combineTimeFrame = (inTime, outTime) => {
    if (!inTime && !outTime) return '';
    const inTime12 = convert24to12(inTime);
    const outTime12 = convert24to12(outTime);
    if (inTime12 && outTime12) return `${inTime12} - ${outTime12}`;
    return inTime12 || outTime12;
  };

  // Helper to split time_frame into in_time and out_time (converting to 24h for input)
  const splitTimeFrame = (timeFrame) => {
    if (!timeFrame) return { in_time: '', out_time: '' };
    const parts = timeFrame.split(' - ');
    return {
      in_time: convert12to24(parts[0]?.trim() || ''),
      out_time: convert12to24(parts[1]?.trim() || '')
    };
  };

  const getSectionConfig = (sectionName, index) => {
    const sectionData = SECTION_CONFIG[sectionName] || getDefaultConfig(index);
    return { ...CARD_THEME, ...sectionData };
  };

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/panchang/sections/`);
      if (!res.ok) throw new Error('Failed to fetch sections');
      const data = await res.json();
      // Handle both paginated response and direct array response
      if (Array.isArray(data)) {
        setSections(data);
      } else if (data && Array.isArray(data.results)) {
        setSections(data.results);
      } else {
        setSections([]);
      }
      setError('');
    } catch (err) {
      setError(`Error fetching sections: ${err.message}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSectionDetails = async (sectionId) => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/panchang/sections/${sectionId}/`);
      if (!res.ok) throw new Error('Failed to fetch section details');
      const data = await res.json();
      setSectionDetails(data);
      setSelectedSection(sectionId);
      setError('');
    } catch (err) {
      setError(`Error fetching section details: ${err.message}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredEntries = () => {
    if (!sectionDetails?.dates) return [];

    return sectionDetails.dates.filter(entry => {
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const nameMatch = entry.name?.toLowerCase().includes(term);
        const descMatch = entry.description?.toLowerCase().includes(term);
        return nameMatch || descMatch;
      }

      return true;
    });
  };

  const filteredEntries = getFilteredEntries();



  const isValidDate = (dateString) => {
    // DD-MM-YYYY format validation
    const regex = /^\d{2}-\d{2}-\d{4}$/;
    if (!regex.test(dateString)) return false;
    const [day, month, year] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  };

  // Helper to add a new date row
  const addDateRow = () => {
    setFormData(prev => ({
      ...prev,
      dates: [...prev.dates, { date: '', in_time: '', out_time: '', description: '' }]
    }));
  };

  // Helper to remove a date row
  const removeDateRow = (index) => {
    setFormData(prev => ({
      ...prev,
      dates: prev.dates.filter((_, i) => i !== index)
    }));
  };

  // Helper to update a specific date row
  const updateDateRow = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      dates: prev.dates.map((row, i) => i === index ? { ...row, [field]: value } : row)
    }));
  };

  const handleAddEntry = async (e) => {
    e.preventDefault();
    const validDates = formData.dates.filter(d => d.date && d.description);
    if (!formData.name || validDates.length === 0) {
      setError('Name, at least one Date with Description are required');
      return;
    }

    try {
      // Submit each date as a separate entry with its own description
      for (const dateEntry of validDates) {
        const apiDate = formatDateForAPI(dateEntry.date);
        const timeFrame = combineTimeFrame(dateEntry.in_time, dateEntry.out_time);

        const payload = {
          name: formData.name,
          date: apiDate,
          description: dateEntry.description,
          time_frame: timeFrame
        };

        const res = await fetch(`${API_BASE_URL}/api/panchang/sections/${selectedSection}/dates/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || `Failed to add entry for date ${apiDate}`);
        }
      }
      await fetchSectionDetails(selectedSection);
      setFormData({ name: '', dates: [{ date: '', in_time: '', out_time: '', description: '' }] });
      setShowAddForm(false);
      setError('');
    } catch (err) {
      setError(`Error adding entry: ${err.message}`);
      console.error(err);
    }
  };

  const handleUpdateEntry = async (e) => {
    e.preventDefault();
    const firstDate = formData.dates[0];
    if (!formData.name || !firstDate?.date || !firstDate?.description) {
      setError('Name, Date and Description are required');
      return;
    }

    try {
      // For editing, use the first date entry (single date update)
      const apiDate = formatDateForAPI(firstDate.date);
      const timeFrame = combineTimeFrame(firstDate.in_time, firstDate.out_time);

      const payload = {
        old_name: editingEntry.name,
        name: formData.name,
        date: apiDate,
        description: firstDate.description,
        time_frame: timeFrame
      };

      const res = await fetch(`${API_BASE_URL}/api/panchang/sections/${selectedSection}/dates/update/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update entry');
      }
      await fetchSectionDetails(selectedSection);
      setFormData({ name: '', dates: [{ date: '', in_time: '', out_time: '', description: '' }] });
      setEditingEntry(null);
      setError('');
    } catch (err) {
      setError(`Error updating entry: ${err.message}`);
      console.error(err);
    }
  };

  const handleDeleteEntry = async (entry) => {
    if (!window.confirm(`Delete "${entry.name}" (${entry.date})?`)) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/panchang/sections/${selectedSection}/dates/delete/`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: entry.name, date: entry.date })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to delete entry');
      }
      await fetchSectionDetails(selectedSection);
      setError('');
    } catch (err) {
      setError(`Error deleting entry: ${err.message}`);
      console.error(err);
    }
  };

  const startEdit = (entry) => {
    setEditingEntry(entry);
    // Convert date from API format (DD-MM-YYYY) to calendar format (YYYY-MM-DD)
    // and split time_frame into in_time and out_time
    const times = splitTimeFrame(entry.time_frame);
    setFormData({
      name: entry.name,
      dates: [{
        date: formatDateForCalendar(entry.date),
        in_time: times.in_time,
        out_time: times.out_time,
        description: entry.description
      }]
    });
    setShowAddForm(false);
  };

  const cancelEdit = () => {
    setEditingEntry(null);
    setFormData({ name: '', dates: [{ date: '', in_time: '', out_time: '', description: '' }] });
  };

  const goBackToSections = () => {
    setSelectedSection(null);
    setSectionDetails(null);
    setShowAddForm(false);
    setEditingEntry(null);
    setFormData({ name: '', dates: [{ date: '', in_time: '', out_time: '', description: '' }] });
    setSearchTerm('');
  };

  if (loading) {
    return (
      <div className="panchang-loading">
        <div className="panchang-loading-spinner"></div>
        <p>Loading Panchang...</p>
      </div>
    );
  }

  return (
    <div className="panchang-container">
      {/* Hero Header */}
      <div className="panchang-hero">
        <div className="panchang-hero-content">
          <div className="panchang-hero-icon">🕉️</div>
          <h1 className="panchang-hero-title">Panchang Management</h1>
          <p className="panchang-hero-subtitle">
            {selectedSection
              ? 'Manage entries in the selected section'
              : 'Select a section to view and manage sacred calendar entries'}
          </p>
        </div>
        <div className="panchang-hero-decoration">
          <div className="decoration-circle circle-1"></div>
          <div className="decoration-circle circle-2"></div>
          <div className="decoration-circle circle-3"></div>
        </div>
      </div>

      {error && (
        <div className="panchang-error">
          <span className="error-icon">⚠️</span>
          {error}
          <button onClick={() => setError('')} className="error-close">×</button>
        </div>
      )}

      {!selectedSection ? (
        /* Section Cards Grid */
        <div className="panchang-sections-grid">
          {sections.map((section, index) => {
            const config = getSectionConfig(section.name, index);
            return (
              <div
                key={section.id}
                className="panchang-section-card"
                onClick={() => fetchSectionDetails(section.id)}
                style={{ '--card-gradient': config.gradient, '--card-color': config.color }}
              >
                <div className="section-card-glow" style={{ background: config.gradient }}></div>
                <div className="section-card-content">
                  <div className="section-card-icon" style={{ background: config.bgLight }}>
                    <span>{config.icon}</span>
                  </div>
                  <h3 className="section-card-title">{section.name}</h3>
                  <p className="section-card-description">{config.description}</p>
                  <div className="section-card-stats">
                    <div className="stat-badge" style={{ background: config.gradient }}>
                      <span className="stat-number">{section.date_count}</span>
                      <span className="stat-label">entries</span>
                    </div>
                  </div>
                  <div className="section-card-action">
                    <span>View Details</span>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Section Details View */
        <div className="panchang-details-view">
          {/* Back Button and Header */}
          <div className="details-header">
            <button onClick={goBackToSections} className="back-button">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              <span>Back to Sections</span>
            </button>
            <div className="details-title-section">
              <div className="details-title-icon">
                {getSectionConfig(sectionDetails?.name, 0).icon}
              </div>
              <div>
                <h2 className="details-title">{sectionDetails?.name}</h2>
                <p className="details-count">{sectionDetails?.dates?.length || 0} entries</p>
              </div>
            </div>
          </div>

          {/* Filters and Actions Bar */}
          <div className="details-toolbar">
            <div className="toolbar-filters">
              <div className="filter-group search-group">
                <label>🔍 Search</label>
                <input
                  type="text"
                  placeholder="Search by name or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="filter-input"
                />
              </div>
            </div>
            <button onClick={() => setShowAddForm(true)} className="add-entry-button">
              <span>+</span> Add New Entry
            </button>
          </div>

          {/* Add/Edit Form */}
          {(showAddForm || editingEntry) && (
            <div className="entry-form-container">
              <div className="entry-form">
                <div className="form-header">
                  <h3>{editingEntry ? '✏️ Edit Entry' : '➕ Add New Entry'}</h3>
                  <button
                    onClick={editingEntry ? cancelEdit : () => setShowAddForm(false)}
                    className="form-close"
                  >
                    ×
                  </button>
                </div>
                <form onSubmit={editingEntry ? handleUpdateEntry : handleAddEntry}>
                  <div className="form-grid">
                    <div className="form-group full-width">
                      <label>Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Enter entry name"
                        required
                      />
                    </div>

                    {/* Multiple Dates Section */}
                    <div className="form-group full-width">
                      <div className="multi-date-header">
                        <label>📅 Dates & Time Frames *</label>
                        {!editingEntry && (
                          <button type="button" className="add-date-btn" onClick={addDateRow}>
                            <span>+</span> Add Another Date
                          </button>
                        )}
                      </div>
                      <div className="multi-date-list">
                        {formData.dates.map((dateRow, index) => (
                          <div key={index} className="date-row">
                            <div className="date-row-number">{index + 1}</div>
                            <div className="date-row-content">
                              <div className="date-row-fields">
                                <div className="date-row-field">
                                  <label>Date *</label>
                                  <input
                                    type="date"
                                    value={dateRow.date}
                                    onChange={(e) => updateDateRow(index, 'date', e.target.value)}
                                    required={index === 0}
                                  />
                                </div>
                                <div className="date-row-field">
                                  <label>In Time</label>
                                  <input
                                    type="time"
                                    value={dateRow.in_time}
                                    onChange={(e) => updateDateRow(index, 'in_time', e.target.value)}
                                  />
                                </div>
                                <div className="date-row-field">
                                  <label>Out Time</label>
                                  <input
                                    type="time"
                                    value={dateRow.out_time}
                                    onChange={(e) => updateDateRow(index, 'out_time', e.target.value)}
                                  />
                                </div>
                              </div>
                              <div className="date-row-description">
                                <label>Description *</label>
                                <textarea
                                  value={dateRow.description}
                                  onChange={(e) => updateDateRow(index, 'description', e.target.value)}
                                  placeholder={`Enter description for date ${index + 1}`}
                                  rows="2"
                                  required={index === 0}
                                />
                              </div>
                            </div>
                            {formData.dates.length > 1 && (
                              <button
                                type="button"
                                className="remove-date-btn"
                                onClick={() => removeDateRow(index)}
                                title="Remove this date"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      {!editingEntry && formData.dates.length > 1 && (
                        <p className="multi-date-hint">
                          💡 {formData.dates.length} dates will be added as separate entries, each with its own description
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn-primary">
                      {editingEntry ? 'Update Entry' : 'Add Entry'}
                    </button>
                    <button
                      type="button"
                      onClick={editingEntry ? cancelEdit : () => setShowAddForm(false)}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Entries Table */}
          <div className="entries-table-container">
            <div className="entries-table-header">
              <h3>📋 Entries</h3>
              <span className="entries-count">{filteredEntries.length} items</span>
            </div>
            <div className="entries-table-wrapper">
              <table className="entries-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Date</th>
                    <th>Time Frame</th>
                    <th>Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.length > 0 ? filteredEntries.map((entry, idx) => (
                    <tr key={idx} className="entry-row">
                      <td className="entry-name">
                        <span className="entry-icon">📌</span>
                        {entry.name}
                      </td>
                      <td className="entry-date">
                        <span className="date-badge">{entry.date}</span>
                      </td>
                      <td className="entry-time">
                        {entry.time_frame ? (
                          <span className="time-badge">🕐 {entry.time_frame}</span>
                        ) : (
                          <span className="no-time">—</span>
                        )}
                      </td>
                      <td className="entry-description">{entry.description}</td>
                      <td className="entry-actions">
                        <button onClick={() => startEdit(entry)} className="action-btn edit-btn" title="Edit">
                          ✏️
                        </button>
                        <button onClick={() => handleDeleteEntry(entry)} className="action-btn delete-btn" title="Delete">
                          🗑️
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="5" className="empty-entries">
                        <div className="empty-state">
                          <span className="empty-icon">📭</span>
                          <p>No entries found</p>
                          <span className="empty-hint">Try adjusting your filters or add a new entry</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PanchangList;
